import React, { useState, useEffect } from 'react';
import { Spin, Modal, message as antMessage, Dropdown } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { 
  XIcon, 
  UserIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  InformationCircleIcon,
  PlusCircleIcon,
  DotsVerticalIcon,
  CheckCircleIcon
} from '@heroicons/react/outline';
import { 
  PlatformIcon
} from '../../design-system/components/data-display/EmailProviderIcons';
import creatorService, { addCreatorToProject, getCreatorsByProject } from '../Creators/creatorService';
import type { Creator } from '../Creators/creatorTypes';
import { useProject } from '../../contexts/ProjectContext';
import { CreatorModal } from '../Creators/CreatorModal';
import { colors, spacing, typography } from '../../design-system';
import {
  ExtensionContainer,
  ExtensionHeader,
  HeaderTitle,
  HeaderActions,
  CloseButton,
  AddButton,
  SelectionHeader,
  SelectionInfo,
  StyledCheckbox,
  ContentArea,
  LoadingContainer,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  ItemsList,
  CreatorItem,
  CreatorCheckbox,
  CreatorAvatar,
  AvatarImage,
  AvatarFallback,
  CreatorInfo,
  CreatorName,
  CreatorStats,
  PlatformBadge,
  CreatorActions,
  ActionButton,
  SelectedCountSpan,
  EmptyStateDescription,
  CreateCreatorButton,
  InfoAlert,
  InfoAlertIcon,
  InfoAlertContent,
  InfoAlertTitle,
  InfoAlertMessage,
  SkeletonCreatorItem,
  SkeletonCheckbox,
  SkeletonAvatar,
  SkeletonContent,
  SkeletonLine
} from './Sidebar.styles';

interface LibraryExtensionProps {
  isOpen: boolean;
  onClose: () => void;
  mainSidebarExpanded?: boolean;
  mode: 'creators';
}

// Helper functions for creator data
const getCreatorPrimaryPlatform = (creator: Creator) => {
  if (creator.tiktok_data?.[0]) {
    return {
      platform: 'tiktok',
      handle: creator.tiktok_data[0].handle,
      name: creator.tiktok_data[0].handle,
      profileImage: creator.tiktok_data[0].profile_image_url,
      followersCount: creator.tiktok_data[0].followers_count || 0
    };
  }
  
  if (creator.instagram_data?.[0]) {
    return {
      platform: 'instagram',
      handle: creator.instagram_data[0].handle,
      name: creator.instagram_data[0].handle,
      profileImage: creator.instagram_data[0].profile_image_url,
      followersCount: creator.instagram_data[0].followers_count || 0
    };
  }
  
  if (creator.twitch_data?.[0]) {
    return {
      platform: 'twitch',
      handle: creator.twitch_data[0].handle,
      name: creator.twitch_data[0].handle,
      profileImage: creator.twitch_data[0].profile_image_url,
      followersCount: creator.twitch_data[0].followers_count || 0
    };
  }
  
  return {
    platform: 'unknown',
    handle: 'Unknown Creator',
    name: 'Unknown Creator',
    profileImage: null,
    followersCount: 0
  };
};

const getCreatorAllPlatforms = (creator: Creator) => {
  const platforms = [];
  
  if (creator.tiktok_data?.[0]) {
    platforms.push({
      platform: 'tiktok',
      handle: creator.tiktok_data[0].handle,
      followersCount: creator.tiktok_data[0].followers_count || 0
    });
  }
  
  if (creator.instagram_data?.[0]) {
    platforms.push({
      platform: 'instagram',
      handle: creator.instagram_data[0].handle,
      followersCount: creator.instagram_data[0].followers_count || 0
    });
  }
  
  if (creator.twitch_data?.[0]) {
    platforms.push({
      platform: 'twitch',
      handle: creator.twitch_data[0].handle,
      followersCount: creator.twitch_data[0].followers_count || 0
    });
  }
  
  return platforms;
};


const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}) => (
  <Modal
    title={title}
    open={isOpen}
    onOk={onConfirm}
    onCancel={onClose}
    okText="Delete"
    cancelText="Cancel"
    okButtonProps={{ danger: true }}
  >
    <p>{message}</p>
  </Modal>
);

export const LibraryExtension: React.FC<LibraryExtensionProps> = ({
  isOpen,
  onClose,
  mainSidebarExpanded = false,
  mode
}) => {
  const { selectedProject } = useProject();
  const mainSidebarWidth = mainSidebarExpanded ? 220 : 56;
  
  // State
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [addingCreators, setAddingCreators] = useState<Set<string>>(new Set());
  const [projectCreatorIds, setProjectCreatorIds] = useState<Set<string>>(new Set());
  
  // Modal states
  const [creatorModalOpen, setCreatorModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCreatorForEdit, setSelectedCreatorForEdit] = useState<Creator | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'creator'; item: Creator } | null>(null);

  // Load data when sidebar opens or project changes
  useEffect(() => {
    if (isOpen) {
      loadCreators();
      if (selectedProject) {
        loadProjectCreators();
      } else {
        setProjectCreatorIds(new Set());
      }
    }
  }, [isOpen, selectedProject?.project_uuid]);


  const loadCreators = async () => {
    setLoading(true);
    try {
      const response = await creatorService.getCreators();
      setCreators(response.data || []);
    } catch (error) {
      console.error('Error loading creators:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectCreators = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await getCreatorsByProject(selectedProject.project_uuid);
      const creatorIds = new Set(response.data.map(c => c.id));
      setProjectCreatorIds(creatorIds);
    } catch (error) {
      console.error('Error loading project creators:', error);
    }
  };


  // Add creator to current project
  const handleAddCreatorToProject = async (creator: Creator) => {
    if (!selectedProject) {
      antMessage.error('Please select a project first');
      return;
    }

    setAddingCreators(prev => new Set(prev).add(creator.id));
    
    try {
      console.log('🎯 [LibraryExtension] Adding creator to project:', { 
        creatorId: creator.id, 
        projectId: selectedProject.project_uuid 
      });
      
      await addCreatorToProject(creator.id, selectedProject.project_uuid);
      
      // Update the project creators set
      setProjectCreatorIds(prev => new Set(prev).add(creator.id));
      
      // Dispatch event to add creator to table (pass creator data)
      window.dispatchEvent(new CustomEvent('creatorAddedToProject', { 
        detail: { creator } 
      }));
    } catch (error: any) {
      console.error('❌ [LibraryExtension] Error adding creator:', error);
      const errorMsg = error.message || error.toString();
      
      // Check if creator is already in project (this is OK)
      if (errorMsg.includes('already') || errorMsg.includes('duplicate') || errorMsg.includes('exists') || errorMsg.includes('Duplicate')) {
        antMessage.info(`${getCreatorPrimaryPlatform(creator).handle} is already in this project`);
      } else {
        antMessage.error('Failed to add creator to project');
      }
    } finally {
      setAddingCreators(prev => {
        const next = new Set(prev);
        next.delete(creator.id);
        return next;
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      await creatorService.deleteCreator(itemToDelete.item.id);
      await loadCreators();
      antMessage.success('Creator deleted successfully');
      
      setDeleteOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      antMessage.error('Failed to delete item');
    }
  };

  const handleImageError = (creatorId: string) => {
    setImageErrors(prev => new Set([...Array.from(prev), creatorId]));
  };

  return (
    <ExtensionContainer $isOpen={isOpen} $mainSidebarWidth={mainSidebarWidth}>
      <ExtensionHeader>
        <HeaderTitle>Creator Library</HeaderTitle>
        <HeaderActions>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
        </HeaderActions>
      </ExtensionHeader>

      {(loading || creators.length > 0) && (
        <SelectionHeader>
          <SelectionInfo>
            <span>{loading ? '—' : creators.length} creators</span>
          </SelectionInfo>
          <AddButton onClick={() => setCreatorModalOpen(true)}>
            <PlusIcon />
            Add New
          </AddButton>
        </SelectionHeader>
      )}

      <ContentArea>
        {loading ? (
          <ItemsList>
            {[...Array(12)].map((_, index) => (
              <SkeletonCreatorItem key={index}>
                <SkeletonAvatar />
                <SkeletonContent>
                  <SkeletonLine $width="65%" $height="11px" />
                  <SkeletonLine $width="45%" $height="9px" />
                </SkeletonContent>
              </SkeletonCreatorItem>
            ))}
          </ItemsList>
        ) : creators.length === 0 ? (
          <>
            <SelectionHeader>
              <div></div>
              <AddButton onClick={() => setCreatorModalOpen(true)}>
                <PlusIcon />
                Add New
              </AddButton>
            </SelectionHeader>
            <EmptyState>
              <EmptyStateIcon>
                <UserGroupIcon />
              </EmptyStateIcon>
              <EmptyStateTitle>No creators in your library</EmptyStateTitle>
              <EmptyStateDescription>
                Add creators to start building your campaigns
              </EmptyStateDescription>
              <CreateCreatorButton onClick={() => setCreatorModalOpen(true)}>
                <PlusIcon />
                Add Creator
              </CreateCreatorButton>
            </EmptyState>
          </>
        ) : (
          <ItemsList>
            {creators.map((creator) => {
              const isAdding = addingCreators.has(creator.id);
              const isInProject = projectCreatorIds.has(creator.id);
              const platform = getCreatorPrimaryPlatform(creator);
              const allPlatforms = getCreatorAllPlatforms(creator);
              const hasImageError = imageErrors.has(creator.id);
              
              // Get contact info - prioritize email from contacts, then shipping email
              const emailContact = creator.contacts?.find(c => c.type === 'email')?.value;
              const shippingEmail = creator.creator_shipping?.email;
              const contactInfo = emailContact || shippingEmail || 'No contact info';
              
              return (
                <CreatorItem
                  key={creator.id}
                  $isSelected={false}
                  $isDragging={false}
                >
                  <CreatorAvatar>
                    {platform.profileImage && !hasImageError ? (
                      <AvatarImage 
                        src={platform.profileImage} 
                        alt={platform.handle}
                        onError={() => handleImageError(creator.id)}
                      />
                    ) : (
                      <AvatarFallback>
                        <UserIcon />
                      </AvatarFallback>
                    )}
                  </CreatorAvatar>
                  
                  <CreatorInfo>
                    <CreatorName>
                      @{platform.handle}
                      <PlatformBadge>
                        {allPlatforms.map((p, index) => (
                          <PlatformIcon key={index} platform={p.platform} size={12} />
                        ))}
                      </PlatformBadge>
                    </CreatorName>
                    <CreatorStats>
                      <span>{contactInfo}</span>
                    </CreatorStats>
                  </CreatorInfo>
                  
                  <CreatorActions>
                    {/* Add to project button */}
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isInProject) {
                          handleAddCreatorToProject(creator);
                        }
                      }}
                      disabled={isAdding || !selectedProject || isInProject}
                      title={
                        !selectedProject 
                          ? 'Select a project first' 
                          : isInProject 
                          ? 'Already in project' 
                          : 'Add to project'
                      }
                      style={{
                        color: isInProject 
                          ? colors.palette.green[500] 
                          : selectedProject 
                          ? colors.palette.indigo[500] 
                          : colors.text.tertiary,
                        opacity: isAdding ? 0.5 : 1,
                        padding: '4px',
                        cursor: isInProject ? 'default' : 'pointer'
                      }}
                    >
                      {isAdding ? (
                        <LoadingOutlined style={{ fontSize: '16px' }} />
                      ) : isInProject ? (
                        <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                      ) : (
                        <PlusCircleIcon style={{ width: '16px', height: '16px' }} />
                      )}
                    </ActionButton>
                    
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          {
                            key: 'edit',
                            label: 'Edit creator',
                            icon: <PencilIcon style={{ width: '14px', height: '14px' }} />,
                            onClick: (e: any) => {
                              e?.domEvent?.stopPropagation();
                              setSelectedCreatorForEdit(creator);
                              setEditModalOpen(true);
                            }
                          },
                          {
                            key: 'delete',
                            label: 'Delete creator',
                            icon: <TrashIcon style={{ width: '14px', height: '14px' }} />,
                            onClick: (e: any) => {
                              e?.domEvent?.stopPropagation();
                              setItemToDelete({ type: 'creator', item: creator });
                              setDeleteOpen(true);
                            },
                            danger: true
                          }
                        ]
                      }}
                    >
                      <ActionButton
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '4px' }}
                      >
                        <DotsVerticalIcon style={{ width: '16px', height: '16px' }} />
                      </ActionButton>
                    </Dropdown>
                  </CreatorActions>
                </CreatorItem>
              );
            })}
          </ItemsList>
        )}
      </ContentArea>

      {/* Creator Modal */}
      <CreatorModal
        isOpen={creatorModalOpen}
        onClose={() => setCreatorModalOpen(false)}
        onSuccess={() => {
          setCreatorModalOpen(false);
          loadCreators();
        }}
        mode="create"
      />

      {/* Edit Modal */}
      <CreatorModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCreatorForEdit(null);
        }}
        onSuccess={() => {
          setEditModalOpen(false);
          setSelectedCreatorForEdit(null);
          loadCreators();
        }}
        mode="edit"
        creator={selectedCreatorForEdit}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Creator"
        message={`Are you sure you want to delete this creator? This action cannot be undone.`}
      />
    </ExtensionContainer>
  );
};